import React, { useContext, useMemo, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import CodeOperationsContext from 'contexts/CodeOperationsContext';
import CodeableContext from 'contexts/CodeableContext';
import { useCodeableRefs } from 'hooks/codeable/useCodeableRefs';
import { useSelectionTextSnapshot } from 'hooks/codeable/useSelectionTextSnapshot';
import { useCodeableTextRef } from 'hooks/useCodeableTextRef';
import { useToasts } from 'ui/toast'
import {
  useCreateExcerpt,
  useAddCodeToExcerpt,
  useRemoveCodeFromExcerpt
} from 'hooks/excerpts/mutations';
import { isCodingVersioningError } from 'utils/versioningErrors';

import { useCreateCode } from 'hooks/codes/mutations/useCreateCode';
import { v4 as uuidv4 } from 'uuid';

// Tracking note: this provider intentionally does NOT carry analytics
// context. Each callsite resolves its own page via the useTrackingPage
// hook and passes { page, surface } as the third argument to these
// operations. Keeps the click handler self-contained and readable —
// no walking the React tree to figure out what gets logged.
const CodeOperationsProvider = ({ projectId, codingUserId, children }) => {
  // Consume the CodeableContext
  const {
    selectedExcerptId,
    select,
    onNewExcerptId,
    onDeselect
  } = useContext(CodeableContext);

  // Get refs for stable function references
  const { selectedExcerptIdRef, selectRef } = useCodeableRefs(selectedExcerptId, select);

  const {
    codeableId,
    codeableType,
  } = select || {};

  // Use our new hook to get data and substring function
  const { getSubstring, body } = useCodeableTextRef(codeableType, codeableId);

  // Capture the selected text at selection time (and drop selections the
  // body changes out from under) — see useSelectionTextSnapshot.
  const getSnapshotText = useSelectionTextSnapshot(select, selectedExcerptId, body, onDeselect);

  // Set up mutations
  const createExcerptMutation = useCreateExcerpt();
  const addCodeToExcerptMutation = useAddCodeToExcerpt();
  const removeCodeFromExcerptMutation = useRemoveCodeFromExcerpt();

  const createCodeMutation = useCreateCode();

  // Adding a code to an excerpt
  // trackingContext: { page, surface } — caller's responsibility.
  const addCodeToExcerpt = useCallback(async (codeId, specificExcerptId = null, trackingContext = null) => {
    // If there is no excerpt selected or no text selected nothing to do
    // and no specific excerpt ID was provided
    if (!specificExcerptId && !selectedExcerptIdRef.current && !selectRef.current) return null;

    const {
      start,
      end,
      codeableId,
      codeableType,
      metadata
    } = selectRef.current || {};

    const userIds = codingUserId ? [codingUserId] : [];

    // If a specific excerpt ID was provided, use that
    if ( specificExcerptId || selectedExcerptIdRef.current) {
      await addCodeToExcerptMutation.mutateAsync({
        codeId,
        excerptId: specificExcerptId || selectedExcerptIdRef.current,
        userIds,
        trackingContext
      }).catch((error) => {
        console.error('Failed to add code to excerpt:', error);
      });
    }
    // If there is no excerpt selected but there is text selected create a new excerpt
    else if (selectRef.current && codeableId && start !== undefined && end !== undefined) {
      // Use the text snapshotted at selection time; fall back to resolving it
      // from the current body only if the snapshot somehow missed this selection.
      const text = getSnapshotText(selectRef.current) ?? getSubstring(start, end);

      if (!text) {
        // Shouldn't happen: the snapshot effect either captured the text at
        // selection time or cleared the selection when the body changed.
        Sentry.captureMessage('Could not find text for excerpt', {
          extra: { start, end, codeableId, codeableType }
        });
        return null;
      }

      // Generate a unique client_id for optimistic updates
      const client_id = uuidv4();

      try {
        const {excerpt} = await createExcerptMutation.mutateAsync({
            start,
            end,
            codeId,
            transcriptId: codeableId,
            codeableType,
            userIds,
            text,
            metadata: metadata || {},
            trackingContext,
            client_id,  // Use client ID for optimistic updates
            onMutate: () => { // I am not sure why the existing onMutate is not working, so passing this in
              // setting the client_id first seemed to create a race condition, and so setting the client_id
              // after the mutation is called seems to work better
              // though conscerned that this might not be the best way to handle it
              onNewExcerptId(client_id);  // set temporary ID
            }
        })
        onNewExcerptId(excerpt?.id);
      } catch (error) {
        // if you did not create the excert, reset the selected excerpt id
        onNewExcerptId(null); // Reset if failed
      }
    }
  }, [
    codingUserId,
    onNewExcerptId,
    selectedExcerptIdRef,
    selectRef,
    getSnapshotText,
    getSubstring
  ]);

  // When a new code is created
  // trackingContext: { page, surface } — caller's responsibility.
  const onNewCode = useCallback((codename, specificExcerptId = null, trackingContext = null) => {
    if (!projectId) return null;

    // Generate a unique client_id for optimistic updates
    const client_id = uuidv4();

    createCodeMutation.mutate({
      name: codename,
      projectId,
      client_id: client_id,
      trackingContext,
    });

    addCodeToExcerpt(client_id, specificExcerptId, trackingContext)
  }, [projectId, addCodeToExcerpt]);

    // In your component, watch for state changes
  const { addToast } = useToasts();

  useEffect(() => {
    if (createCodeMutation.isError) {
      addToast('Something went wrong. Please refresh and try again.', { appearance: 'error' });
    }
  }, [createCodeMutation.isError]);

  useEffect(() => {
    if (createExcerptMutation.isError) {
      // Only show toast if it's not a coding version mismatch error (which shows a modal instead)
      if (!isCodingVersioningError(createExcerptMutation.error)) {
        addToast('Something went wrong, please reload and try again.', { appearance: 'error' });
      }
    }
  }, [createExcerptMutation.isError, createExcerptMutation.error]);

  useEffect(() => {
    if (addCodeToExcerptMutation.isError) {
      addToast('Failed to add code to excerpt. Please try again.', { appearance: 'error' });
    }
  }, [addCodeToExcerptMutation.isError]);


  // Removing a code from an excerpt.
  // trackingContext: { page, surface } — caller's responsibility.
  const onRemoveCode = useCallback(async (codeId, specificExcerptId = null, trackingContext = null) => {
    if (!selectedExcerptIdRef.current && !specificExcerptId) return null;

    const userIds = codingUserId ? [codingUserId] : [];

    await removeCodeFromExcerptMutation.mutateAsync({
      codeId,
      excerptId: specificExcerptId || selectedExcerptIdRef.current,
      userIds,
      trackingContext
    }).catch((error) => {
      console.error('Failed to remove code from excerpt:', error);
    });
  }, [codingUserId, selectedExcerptIdRef]);

  // Handle excerpt selection
  const onExcerptSelected = useCallback((excerptId) => {
    onNewExcerptId(excerptId);
  }, [onNewExcerptId]);

  const contextValue = useMemo(() => ({
    onExcerptSelected,
    onNewCode,
    addCodeToExcerpt,
    onRemoveCode,
    getSubstring,
    isCreatingExcerpt: createExcerptMutation.isPending,
    isAddingCode: addCodeToExcerptMutation.isPending,
    isRemovingCode: removeCodeFromExcerptMutation.isPending,
    createExcerptError: createExcerptMutation.error,
  }), [
    onExcerptSelected,
    onNewCode,
    addCodeToExcerpt,
    onRemoveCode,
    getSubstring,
    createExcerptMutation.isPending,
    addCodeToExcerptMutation.isPending,
    removeCodeFromExcerptMutation.isPending,
    createExcerptMutation.error
  ]);

  return (
    <CodeOperationsContext.Provider value={contextValue}>
      {children}
    </CodeOperationsContext.Provider>
  );
};

export default CodeOperationsProvider;
